import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { Cart, EnvConfig } from '@shared/definitions';
import { map, Observable } from 'rxjs';

import { SeatType } from '../models/seat';
import { CartDto } from './dto/products.dto';
import { SeatTypeDto } from './dto/seats.dto';

export interface SeatsReservationResponse {
  show_id: string;
  shop_id: string;
  area_id: string;
  cart_id: string;
  session_id: string;
  source: string | null;
  reserved_until: string | null; // ISO date format, null when the cart never expires
  server_time: string; // ISO date format
  cart: Cart;
  reserved_seat_products: ReservedSeatProduct[];
  cart_line_id: string;
}

export interface ReservedSeatProduct {
  seat_id: string;
  product_id: string;
  cart_line_id: string;
  row: number;
  row_label: string;
  column: number;
  column_label: string;
  group: string;
}

export interface RemoveSeatReservationResponse {
  cart: CartDto;
}

/**
 * Seat Response
 * @property {string} seat_import_id - The seat import id is the id of the seat in the area  svg file
 */
export interface SeatResponse {
  id: string;
  seat_type_id: string;
  row: number;
  row_label: string;
  column: number;
  column_label: string;
  group: string;
  area_id: string;
  seat_import_id: string;
}

@Injectable({
  providedIn: 'root',
})
export class SeatsService {
  constructor(@Inject(ENV_CONFIG) private readonly config: EnvConfig, private readonly client: HttpClient) {}

  getSeats(shopId: string, areaId: string): Observable<SeatResponse[]> {
    return this.client
      .get<{ data: SeatResponse[] }>(`${this.config.api}/v1.0/seats`, {
        params: {
          shop: shopId,
          area: areaId,
        },
      })
      .pipe(map((response: { data: SeatResponse[] }) => response.data));
  }

  reserveSeat(
    item: { seatId: string; productId: string | null },
    shopId: string,
    showId: string,
    areaId: string,
    cartId: string | null,
    posId: string,
    parentCartLineId: string = null,
    sessionId: string = null
  ): Observable<SeatsReservationResponse> {
    return this.client
      .post<SeatsReservationResponse>(`${this.config.api}/v1.0/seats/reservations`, {
        seat_id: item.seatId,
        product_id: item.productId,
        show_id: showId,
        shop_id: shopId,
        area_id: areaId,
        cart_id: cartId,
        pos_id: posId,
        cart_line_id: parentCartLineId,
        ...(sessionId ? { shop_session_id: sessionId } : {}),
      })
      .pipe(
        map((response: SeatsReservationResponse) => {
          return {
            ...response,
            // TODO: This is a workaround to fix the response type
            cart_line_id: response.reserved_seat_products.find((i) => i.seat_id === item.seatId)?.cart_line_id,
          };
        })
      );
  }

  removeReservation(
    reservationId: string,
    posId: string,
    onlySeatReservation = true
  ): Observable<RemoveSeatReservationResponse> {
    return this.client.delete<RemoveSeatReservationResponse>(`${this.config.api}/v1.0/reservations/${reservationId}`, {
      params: { seatOnly: onlySeatReservation },
      body: {
        pos_id: posId,
      },
    });
  }

  /**
   * Prefetched during HTML parse by the inline script in the three `index.*.html` files and claimed
   * by `BootstrapPrefetchInterceptor`, which matches this URL literally. Change it and the head
   * start is silently lost — the call just runs normally — so keep the three index files and
   * the interceptor in sync. See `application/bootstrap-prefetch.ts`.
   */
  getReservations(showId: string): Observable<SeatResponse[]> {
    return this.client.get<SeatResponse[]>(`${this.config.api}/v1.0/seats/reservations/${showId}`);
  }

  validateCartReservations(cartId: string): Observable<any> {
    return this.client.post(`${this.config.api}/v1.0/seats/${cartId}/validate`, null);
  }

  getTypes(shopId: string, areaId: string): Observable<SeatType[]> {
    return this.client
      .get<{ data: SeatTypeDto[] }>(`${this.config.api}/v1.0/seats/types`, {
        params: {
          shop: shopId,
          area: areaId,
        },
      })
      .pipe(
        map((response) => {
          return response?.data?.map((type) => ({
            id: type.id,
            name: type.name,
            colorDark: type.color_dark_theme,
            colorLight: type.color_light_theme,
            isDefault: type.is_default,
          }));
        })
      );
  }
}
